import type { ElementPart } from 'lit'

import { Directive, directive } from 'lit/directive.js'
import { nothing } from 'lit'

const SINGULAR_ARIA_ATTRIBUTES = ['activeDescendant']

type AriaElementsDirectiveOptions = {
	flowTo?: string
	details?: string
	controls?: string
	labelledBy?: string
	describedBy?: string
	errorMessage?: string
	activeDescendant?: string
}

class AriaElementsDirective extends Directive {
	#elements: Record<keyof AriaElementsDirectiveOptions, HTMLElement[]> =
		{} as Record<keyof AriaElementsDirectiveOptions, HTMLElement[]>

	#prevAttributes: Record<keyof AriaElementsDirectiveOptions, string> =
		{} as Record<keyof AriaElementsDirectiveOptions, string>

	render(_attributes: AriaElementsDirectiveOptions) {
		return nothing
	}

	update(part: ElementPart, [attributes]: Parameters<this['render']>) {
		// We need to wait for the next frame to ensure that the target element and
		// all of the referenced aria elements have mounted
		window.requestAnimationFrame(() => {
			for (const [attribute, value] of Object.entries(attributes)) {
				// Prevent excessively updating the same attribute
				if (this.#prevAttributes[attribute] === value) continue
				this.#prevAttributes[attribute] = value

				const property = `aria${attribute.charAt(0).toUpperCase()}${attribute.slice(1)}Element${SINGULAR_ARIA_ATTRIBUTES.includes(attribute) ? '' : 's'}`

				if (!value) {
					// Remove elements added by this directive but persist any other
					// elements added by others
					const elements = part.element[property]?.filter(
						(element) => !this.#elements[attribute]?.includes(element)
					)

					if (SINGULAR_ARIA_ATTRIBUTES.includes(attribute)) {
						part.element[property] = elements?.[0]
					} else {
						part.element[property] = elements
					}

					this.#elements[attribute] = []

					continue
				}

				let node = part.element.getRootNode() as Element | ParentNode
				const ids = value.trim().split(/\s+/)
				const found: Record<string, HTMLElement> = {}

				while (node && ids.length) {
					for (let i = 0; i < ids.length; i++) {
						const id = ids[i]
						const element = node.querySelector(`#${id}`)
						if (element) {
							found[id] = element as HTMLElement
							ids.splice(i, 1)
							i--
						}
					}

					const root = node.getRootNode()
					if (root instanceof ShadowRoot) {
						node = root.host
					} else {
						node = node.parentNode
					}
				}

				for (const id of ids) {
					if (!found[id]) {
						const root = part.element.getRootNode()
						console.error(
							`<${root.nodeName.toLowerCase()}>: Could not find #${id} (${property})`
						)
					}
				}

				const elements = Object.values(found)

				this.#elements[attribute] = elements

				if (SINGULAR_ARIA_ATTRIBUTES.includes(attribute)) {
					part.element[property] = elements[0]
				} else {
					part.element[property] = elements.concat(part.element[property] ?? [])
				}
			}
		})
	}
}

/**
 * Bind aria elements (by id) to an element by walking up the DOM to find the
 * elements in ancesstor hosts and attach them using the aria reflection API
 *
 * @see https://wicg.github.io/aom/aria-reflection-explainer.html
 *
 * @example
 * class MyComponent extends LfuiElement {
 *   @property() lfAriaLabelledByElements: string
 *
 *   render() {
 *     return html`
 *       <input type="text" ${ariaElements({ labelledBy: this.lfAriaLabelledByElements })} />
 *     `
 *   }
 * }
 */
export default directive(AriaElementsDirective)
